import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { notes } from '../data/profileData';

const noteBodies = {
  'policy-is-not-control': {
    body: [
      `A policy tells you what an organization intends to do. It defines requirements, assigns responsibilities, and sets expectations. It is the starting point of a control, not the control itself.`,
      `What a policy cannot do is confirm that the requirement is actually met. For that, you need to look at implementation. Is the process running? Is it producing records? Are the records consistent with the policy's requirements? Does anyone review them?`,
      `In practice, gaps appear at each of these points. A well-written policy can coexist with a process that has not run in months, or with logs that exist but are never reviewed. Both situations would look compliant on a checklist that only asks "is there a policy?"`,
      `Control assurance is the work of closing the gap between stated intent and demonstrated practice. It requires different evidence at each step: documentation to establish intent, records to show execution, tests to verify effectiveness, and follow-up to confirm that exceptions are handled.`,
      `When evidence stops at the policy level, the assessment has only confirmed intent. That is worth noting, but it should not be confused with confirmed compliance or operational effectiveness.`,
    ],
  },
  'actionable-recommendations': {
    body: [
      `An assessment produces findings. Findings describe what the evidence shows, where the gap is, and what the gap means for the organization. A recommendation tells someone what to do about it.`,
      `The distance between those two things is often wider than it appears. A finding that says "backup procedures are not consistently followed" is accurate. A recommendation that says "improve backup procedures" adds almost nothing.`,
      `For a recommendation to be actionable, it needs to answer a few questions concretely: what needs to change, at what level, by whom, and within what timeframe. It also needs to connect the action to the control objective, so whoever is responsible can understand why the change matters, not just that it was flagged.`,
      `A finding about inconsistent backup execution might lead to different recommendations depending on context. If the process is unclear, the recommendation might target the procedure. If the process is clear but unmonitored, it might target oversight. If it is monitored but exceptions are not escalated, it might target reporting structure. Each of these is a different action, aimed at a different part of the problem.`,
      `Specificity is what separates a recommendation someone can act on from an observation someone can acknowledge and move past.`,
    ],
  },
  'technical-findings-to-management': {
    body: [
      `Technical findings are written for people who understand the system. Management decisions are made by people who need to understand the risk. Those two groups are not always the same, and writing for one often does not serve the other.`,
      `A finding about a misconfigured firewall rule means something precise to a network engineer. To a risk committee, it needs to be framed differently: what exposure does this create, how likely is it to be relevant, and what does addressing it require in terms of resources and tradeoffs.`,
      `The translation is not about simplifying the finding. It is about connecting the technical detail to the decision that needs to be made. Management does not need to understand the firewall configuration to decide whether to prioritize remediation. They need to understand what the gap exposes, how it compares to other gaps, and what the options are.`,
      `This also means accepting that the translated version carries less technical precision. That is intentional. Precision in the technical finding is important for whoever does the remediation work. Clarity in the management summary is important for whoever decides what to prioritize.`,
      `The failure mode to avoid is writing management summaries that are technically accurate but not decision-ready, or simplifying so much that the finding loses its meaning. The goal is to preserve the decision-relevant information while making it accessible at the right level.`,
    ],
  },
};

export default function NoteDetail() {
  const { slug } = useParams();
  const note = notes.find((n) => n.slug === slug);
  const content = noteBodies[slug];

  if (!note || !content) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-sm text-stone-400 mb-4">Note not found.</p>
          <Link
            to="/"
            className="text-[11px] font-mono uppercase tracking-widest text-stone-500 hover:text-stone-900 transition-colors border-b border-stone-300 pb-px"
          >
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-6 py-16 md:py-24">
      <div className="max-w-2xl mx-auto">
        {/* Back */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-stone-400 hover:text-stone-700 transition-colors mb-12 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 rounded"
        >
          <span aria-hidden="true">←</span>
          <span>Back</span>
        </Link>

        {/* Header */}
        <header className="mb-10 border-b border-stone-200 pb-8">
          <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-stone-400 mb-3">
            Notes · Draft
          </p>
          <h1 className="text-2xl md:text-3xl font-serif text-stone-900 leading-snug">
            {note.title}
          </h1>
        </header>

        {/* Body */}
        <div className="space-y-5">
          {content.body.map((para, i) => (
            <p key={i} className="text-[15px] text-stone-600 leading-[1.8]">
              {para}
            </p>
          ))}
        </div>

        {/* Draft notice */}
        <p className="mt-12 text-[11px] text-stone-400 font-mono italic">
          This note is a draft and has not been reviewed for publication.
        </p>
      </div>
    </div>
  );
}

